import Button from "@app/components/button";
import CouponSuccessModal from "@app/components/coupon-success-modal";
import InputCouponModal from "@app/components/input-coupon-modal";
import InputReferredModal from "@app/components/input-referred-modal";
import MenuLinkItem from "@app/components/menu-link-item";
import {
  FeatureName,
  MenuLabel,
  RoutePath,
  SystemMessage,
} from "@app/constants";
import { UserMenuList } from "@app/constants/sidebar";
import UpgradeIcon from "@app/icons/upgrade";
import AffiliateService from "@app/services/http/affiliate.service";
import FeatureInformationService from "@app/services/http/feature-information.service";
import { openPortalDialog } from "@app/services/modal.service";
import { selectAuth } from "@app/store/auth";
import { FeatureInformation } from "@app/types";
import { Images } from "@assets/images";
import { isNumber } from "@core/utils/util";
import clsx from "clsx";
import { MouseEvent, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { FiInfo } from "react-icons/fi";
import { Tooltip } from "react-tooltip";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useCopyToClipboard } from "@core/hooks/use-copy-to-clipboard.hook";
import { addToast } from "@app/components/toast/toast.service";

function UserProfileLayout() {
  const location = useLocation();
  const navigate = useNavigate();

  const userInfo = useSelector(selectAuth);

  const [title, setTitle] = useState("");
  const [featureInformation, setFeatureInformation] = useState<
    FeatureInformation[]
  >([]);

  const [, copy] = useCopyToClipboard();
  const { abortController } = useCancellationRequest();

  useEffect(() => {
    FeatureInformationService.get(abortController.signal).then((res) => {
      setFeatureInformation(res.data.items);
    });
  }, []);

  useEffect(() => {
    switch (true) {
      case location.pathname.includes(RoutePath.USER_ACCOUNT):
        setTitle(MenuLabel.USER_ACCOUNT);
        break;
      case location.pathname.includes(RoutePath.USER_LIBRARY):
        setTitle(MenuLabel.USER_LIBRARY);
        break;
      case location.pathname.includes(RoutePath.USER_WALLET):
        setTitle(MenuLabel.USER_WALLET);
        break;
      case location.pathname.includes(RoutePath.USER_AFFILIATE):
        setTitle("Rút tiền");
        break;
      case location.pathname.includes(RoutePath.USER_TRANSACTION):
        setTitle(MenuLabel.USER_TRANSACTION);
        break;
      case location.pathname.includes(RoutePath.USER_RULES):
        setTitle(MenuLabel.USER_RULES);
        break;
      case location.pathname.includes(RoutePath.USER_POLICIES):
        setTitle(MenuLabel.USER_POLICIES);
        break;
      default:
        setTitle("");
        break;
    }
  }, [location]);

  const handleUpgradeClick = () => {
    navigate(RoutePath.UPGRADE);
  };

  const handleCouponClick = () => {
    const modalObs = openPortalDialog(InputCouponModal);

    modalObs.afterClosed().subscribe((data) => {
      if (data?.couponCode) {
        AffiliateService.postAffiliate(data.couponCode).then((res) => {
          const obs = openPortalDialog(CouponSuccessModal, {
            message: res.data.message,
          });

          obs.afterClosed().subscribe(() => {
            window.location.reload();
          });
        });
      }
    });
  };

  const handleReferredClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    const modalObs = openPortalDialog(InputReferredModal);

    modalObs.afterClosed().subscribe((data) => {
      if (data?.referredCode) {
        AffiliateService.postAffiliate(data.referredCode).then((res) => {
          const obs = openPortalDialog(CouponSuccessModal, {
            message: res.data.message,
          });

          obs.afterClosed().subscribe(() => {
            window.location.reload();
          });
        });
      }
    });
  };

  const rakingFeatureInformation = useMemo(() => {
    return featureInformation.find(
      (item) => item.featureName === FeatureName.RAKING
    );
  }, [featureInformation]);

  const handleCopyClick = (text?: string) => {
    if (!text) return;

    copy(text).then(() => {
      addToast({ text: SystemMessage.COPY_SUCCESSFULLY });
    });
  };

  return (
    <div className="flex justify-center md:px-[10px] lg:px-[30px] xl:px-[100px] mt-12 min-h-[calc(100vh-316px)]">
      <div className="flex flex-col text-center min-w-[294px] border-r-2 border-gray-300 pr-5">
        <div className="flex gap-3">
          <div className="relative">
            <img
              className="w-[80px] h-[80px] rounded-full object-cover"
              src={userInfo?.thumbnail}
              alt=""
            />
            <div
              className={clsx(
                "absolute -bottom-1 left-1/2 -translate-x-1/2 w-[49px] h-5 font-[SVN-Gilroy] font-medium text-xs text-white rounded-[10px]",
                {
                  "bg-[linear-gradient(180deg,#33AC80,#49C472)]":
                    !userInfo.isVip,
                  "bg-[linear-gradient(180deg,#FC8A20_25.48%,#FFDD2D_116.64%)]":
                    !!userInfo.isVip,
                }
              )}
            >
              {userInfo.isVip ? "VIP" : "Free"}
            </div>
          </div>
          <div className="flex flex-col items-start">
            <div className="text-[SVN-Gilroy] font-semibold text-lg text-[#333B47] tracking-[0.027px]">
              {userInfo?.fullName}
            </div>
            {!!userInfo.bookasId && (
              <>
                <div className="text-[SVN-Gilroy] font-medium text-sm text-[#333B47] tracking-[0.027px]">
                  ID người giới thiệu:
                </div>
                <div className="flex items-center gap-1">
                  <div className="text-[SVN-Gilroy] font-medium text-sm text-[#333B47] tracking-[0.027px]">
                    {userInfo?.bookasId}
                  </div>
                  <img
                    src={Images.CopyPayment.default}
                    alt=""
                    className="cursor-pointer"
                    onClick={() => handleCopyClick(userInfo?.linkAffiliate)}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <div className="ml-5 mt-4">
          {!!userInfo?.barge?.thumbnail && (
            <div>
              <img className="w-10" src={userInfo.barge.thumbnail} alt="" />
            </div>
          )}
        </div>
        <div>
          {userInfo.isVip && userInfo.expiredDateVip ? (
            <div className="flex gap-2 flex-col items-center mt-2">
              <div className="text-sm w-[116px] h-9 bg-gradient-to-b from-[#FC8B22] to-[#FFDD2D] flex justify-center items-center gap-1 rounded-xl text-white font-semibold">
                <UpgradeIcon color="white" />
                <div>PREMIUM</div>
              </div>
              <div className="font-bold">{userInfo.expiredDateVip}</div>
            </div>
          ) : (
            <Button
              width="auto"
              className="mt-5 !w-[152px] text-sm !h-11 font-bold bg-gradient-to-b from-[#8092e4] to-[#38499b] hover:from-[#8997dd] hover:to-[#4559be] !rounded-[22px]"
              label="Trở thành hội viên"
              labelClassName="font-[SVN-Gilroy] tracking-[0.028px]"
              onClick={handleUpgradeClick}
            />
          )}
          <div className="mt-5 p-4 bg-[#FFFEE6] rounded-xl border-[2px] border-solid border-[#FC8A20]">
            <div className="flex justify-between">
              <div className="flex items-center gap-1">
                <div className="text-[#171A20] font-[SVN-Gilroy] font-semibold text-sm tracking-[0.028px]">
                  {userInfo?.barge?.name?.toUpperCase()}
                </div>
                {!!userInfo?.barge?.thumbnail && (
                  <div>
                    <img
                      className="w-[18px]"
                      src={userInfo.barge.thumbnail}
                      alt=""
                    />
                  </div>
                )}
              </div>
              <div className="text-[#171A20] font-[SVN-Gilroy] font-semibold text-sm tracking-[0.028px]">
                {isNumber(userInfo?.barge?.progress) &&
                  `${Number(Number(userInfo.barge.progress).toFixed(2))}/100`}
              </div>
            </div>
            {isNumber(userInfo?.barge?.progress) && (
              <div>
                <div className="mt-4 w-full bg-[#D5DBE2] rounded-full h-[6px]">
                  <div
                    className="bg-[linear-gradient(180deg,#FC8A20,#FFDD2D)] h-[6px] rounded-full"
                    style={{ width: `${Number(userInfo.barge.progress)}%` }}
                  ></div>
                </div>
              </div>
            )}
            <div className="mt-4 flex items-center justify-between">
              <div className="text-[#424E62] font-[SVN-Gilroy] font-medium text-xs">
                Tích cực nghe sách để lên hạng{" "}
                <span className="font-bold">
                  {userInfo?.barge?.nameNextBarge}
                </span>
              </div>
              {!!userInfo?.barge?.thumbnail && (
                <div>
                  <img
                    className="w-[18px]"
                    src={userInfo.barge.thumbnailNextBarge}
                    alt=""
                  />
                </div>
              )}
              {!userInfo?.barge?.thumbnail && (
                <>
                  <div
                    data-tooltip-id={"infoUnRankIcon"}
                    className="cursor-pointer"
                  >
                    <FiInfo />
                  </div>
                  <Tooltip
                    id={"infoUnRankIcon"}
                    place="top-end"
                    variant="light"
                    content={rakingFeatureInformation?.information}
                    className="max-w-[200px] rounded-[4px]"
                  />
                </>
              )}
            </div>
          </div>
          <div
            className={clsx(
              "mt-5 h-9 w-full rounded-[20px] border border-solid border-[#FC8A20] flex justify-center items-center cursor-pointer",
              "text-[#FC8A20] font-[SVN-Gilroy] text-sm font-bold tracking-[0.028px] hover:bg-[#fff1e5]"
            )}
            onClick={handleCouponClick}
          >
            Nhận ưu đãi
          </div>
          <div className="mt-5 flex justify-center items-center gap-3">
            <div className="size-11 bg-[#E1EAF8] flex justify-center items-center rounded-[40px]">
              <img src={Images.VolumnHighIcon.default} alt="" />
            </div>
            <div className="font-[SVN-Gilroy] font-medium text-sm">
              <span>Đã nghe: </span>
              <span className="text-[#5A82D7]">
                {userInfo.totalListenedTime}
              </span>
            </div>
          </div>
          <hr className="mt-5 h-0.5 mx-auto bg-[#C0C5CF] w-60 text-center" />
        </div>
        <div className="mt-4">
          <div className="flex flex-col gap-2">
            {!!UserMenuList.length &&
              UserMenuList.map((item, index) => (
                <MenuLinkItem
                  className="!h-11 !px-4"
                  labelClassName="font-[SVN-Gilroy] font-medium"
                  key={index}
                  {...item}
                  onClick={
                    item.linkTo === RoutePath.REFERRER
                      ? handleReferredClick
                      : undefined
                  }
                />
              ))}
          </div>
        </div>
      </div>
      <div className="pl-6 w-full">
        <div className="font-bold text-[#171A20] text-xl font-[SVN-Gilroy]">
          {title}
        </div>
        <Outlet />
      </div>
    </div>
  );
}

export default UserProfileLayout;
